import { createContext, useCallback, useContext } from 'react'
import {
  DescMessage,
  DescMethodUnary,
  MessageInitShape,
  MessageShape,
} from '@bufbuild/protobuf'
import {
  callUnaryMethod,
  UseMutationOptions,
  useTransport,
} from '@connectrpc/connect-query'
import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation as tsUseMutation } from '@tanstack/react-query'

import { getAccessToken, setAccessToken, shouldRefresh } from '@lib/auth'

import { IntermediateSession, SessionClaims } from '@gen/api/v1/api_pb'
import { ConnectError } from '@connectrpc/connect'

const IntermediateSessionContext = createContext<
  IntermediateSession | undefined
>(undefined)
export const IntermediateSessionProvider = IntermediateSessionContext.Provider
export const useIntermediateSession = () => {
  const intermediateSession = useContext(IntermediateSessionContext)
  return intermediateSession
}

const SessionContext = createContext<SessionClaims | undefined>(undefined)
export const SessionProvider = SessionContext.Provider
export const useSession = () => {
  const session = useContext(SessionContext)
  return session
}

export const useAuthenticatedMutation = <
  I extends DescMessage,
  O extends DescMessage,
  Ctx = unknown,
>(
  schema: DescMethodUnary<I, O>,
  { transport, ...queryOptions }: UseMutationOptions<I, O, Ctx> = {},
): UseMutationResult<
  MessageShape<O>,
  ConnectError,
  MessageInitShape<I>,
  Ctx
> => {
  const transportFromCtx = useTransport()
  const transportToUse = transport ?? transportFromCtx
  const mutationFn = useCallback(
    async (input: MessageInitShape<I>) =>
      callUnaryMethod(transportToUse, schema, input),
    [transportToUse, schema],
  )
  return tsUseMutation({
    ...queryOptions,
    mutationFn,
    onMutate: async (variables: any) => {
      const accessToken = getAccessToken()
      if (!accessToken || shouldRefresh(accessToken)) {
        const response = await fetch('/api/v1/refresh')

        if (!response.ok) {
          throw new Error('Failed to refresh session')
        }

        const { accessToken: newAccessToken } = await response.json()
        setAccessToken(newAccessToken)
      }

      return variables
    },
  } as any)
}
